/** Category picker — searchable dropdown с tree-indent.
 *
 * Без зависимости от @radix-ui/react-popover (которого нет в admin'е).
 * Используется cmdk для поиска + custom positioned dropdown с
 * click-outside detection.
 *
 *  PrimaryCategoryPicker  — Combobox (одиночный, поиск)
 *  SecondaryCategoriesPicker — Multi-select (чипы выбранных + add-popup)
 */

import { Command } from "cmdk";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface AdminCategoryRaw {
  id: number;
  parent_id: number | null;
  slug: string;
  products_count?: number;
  translations: Array<{ language: string; name: string }>;
}

interface CategoryNode {
  id: number;
  name: string;
  parent_id: number | null;
  products_count?: number;
}

interface FlatNode {
  id: number;
  name: string;
  depth: number;
  parent_id: number | null;
  products_count?: number;
  /** Полный путь "Корень › Под › Текущая" — используется в поиске и trigger */
  path: string;
}

function useCategoriesFlat() {
  return useQuery({
    queryKey: ["admin-categories-flat"],
    queryFn: async () => {
      const { data } = await api.get<AdminCategoryRaw[]>("/categories");
      // Нормализуем: пытаемся взять русский, потом любой translation,
      // потом slug как fallback.
      return data.map<CategoryNode>((c) => {
        const ruName = c.translations.find((t) => t.language === "ru")?.name;
        const anyName = c.translations[0]?.name;
        return {
          id: c.id,
          parent_id: c.parent_id,
          name: ruName ?? anyName ?? c.slug,
          products_count: c.products_count,
        };
      });
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Превращает плоский список с parent_id в упорядоченный tree-walk. */
function flatten(categories: CategoryNode[]): FlatNode[] {
  const byParent = new Map<number | null, CategoryNode[]>();
  for (const c of categories) {
    const arr = byParent.get(c.parent_id ?? null) ?? [];
    arr.push(c);
    byParent.set(c.parent_id ?? null, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }

  const result: FlatNode[] = [];
  function walk(parentId: number | null, depth: number, parentPath: string) {
    const kids = byParent.get(parentId) ?? [];
    for (const c of kids) {
      const path = parentPath ? `${parentPath} › ${c.name}` : c.name;
      result.push({
        id: c.id,
        name: c.name,
        depth,
        parent_id: c.parent_id,
        products_count: c.products_count,
        path,
      });
      walk(c.id, depth + 1, path);
    }
  }
  walk(null, 0, "");
  return result;
}

/** Хук для close-on-click-outside. */
function useClickOutside(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  return ref;
}

// ─── Common command dropdown styling ──────────────────────────────────────

const cmdInputCls =
  "h-9 w-full border-b border-border bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground";
const cmdListCls = "max-h-72 overflow-auto p-1";
const cmdItemCls =
  "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent aria-selected:bg-accent";
const cmdEmptyCls = "px-3 py-3 text-sm text-muted-foreground text-center";

// ─── Primary category combobox ─────────────────────────────────────────────

export function PrimaryCategoryPicker({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (id: number) => void;
}) {
  const { data: categories, isLoading } = useCategoriesFlat();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));

  const flat = useMemo(
    () => (categories ? flatten(categories) : []),
    [categories],
  );
  const selected = flat.find((c) => c.id === value);

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full justify-between font-normal"
        disabled={isLoading}
      >
        <span className="truncate text-left">
          {selected ? selected.path : "Выберите категорию…"}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-md border border-border bg-popover shadow-md">
          <Command shouldFilter>
            <Command.Input
              autoFocus
              placeholder="Поиск категории…"
              className={cmdInputCls}
            />
            <Command.List className={cmdListCls}>
              <Command.Empty className={cmdEmptyCls}>Ничего не найдено</Command.Empty>
              {flat.map((c) => (
                <Command.Item
                  key={c.id}
                  value={c.path}
                  onSelect={() => {
                    onChange(c.id);
                    setOpen(false);
                  }}
                  className={cmdItemCls}
                >
                  <Check
                    className={`h-4 w-4 ${
                      c.id === value ? "opacity-100" : "opacity-0"
                    }`}
                  />
                  <span style={{ paddingLeft: c.depth * 12 }}>
                    {c.name}
                    {c.products_count != null && c.products_count > 0 && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        · {c.products_count}
                      </span>
                    )}
                  </span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </div>
      )}
    </div>
  );
}

// ─── Secondary multi-select with chips ─────────────────────────────────────

export function SecondaryCategoriesPicker({
  primaryId,
  value,
  onChange,
}: {
  primaryId: number | null;
  value: number[];
  onChange: (ids: number[]) => void;
}) {
  const { data: categories, isLoading } = useCategoriesFlat();
  const [open, setOpen] = useState(false);
  const ref = useClickOutside(open, () => setOpen(false));

  const flat = useMemo(
    () =>
      categories
        ? flatten(categories).filter((c) => c.id !== primaryId)
        : [],
    [categories, primaryId],
  );

  const selectedSet = new Set(value);
  const selectedItems = flat.filter((c) => selectedSet.has(c.id));

  function toggle(id: number) {
    if (selectedSet.has(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {selectedItems.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            Не выбрано (товар будет показан только в основной категории)
          </span>
        ) : (
          selectedItems.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs"
              title={c.path}
            >
              {c.name}
              <button
                type="button"
                onClick={() => toggle(c.id)}
                className="rounded-full transition hover:bg-background"
                aria-label={`Убрать ${c.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="relative inline-block" ref={ref}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          disabled={isLoading || primaryId == null}
        >
          + Добавить категорию
        </Button>
        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border border-border bg-popover shadow-md">
            <Command shouldFilter>
              <Command.Input
                autoFocus
                placeholder="Поиск категории…"
                className={cmdInputCls}
              />
              <Command.List className={cmdListCls}>
                <Command.Empty className={cmdEmptyCls}>Ничего не найдено</Command.Empty>
                {flat.map((c) => {
                  const checked = selectedSet.has(c.id);
                  return (
                    <Command.Item
                      key={c.id}
                      value={c.path}
                      onSelect={() => toggle(c.id)}
                      className={cmdItemCls}
                    >
                      <Check
                        className={`h-4 w-4 ${
                          checked ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <span style={{ paddingLeft: c.depth * 12 }}>
                        {c.name}
                      </span>
                    </Command.Item>
                  );
                })}
              </Command.List>
            </Command>
          </div>
        )}
      </div>
    </div>
  );
}
